import { defineStore } from 'pinia';
import { nftBalanceList } from '@/api/wallet';
import { useProfileStore } from '@/stores/profile';

function defaultState() {
  return {
    remoteList: [],
    nftList: [],
    currentNft: null,
    currentNftAsset: null
  };
}

function normalizeNft(item = {}) {
  return {
    ...item,
    contractAddress: item?.contractAddress || item?.address || '',
    protocolType: item?.protocolType || '',
    symbol: item?.symbol || '',
    name: item?.name || item?.symbol || 'NFT',
    img: item?.img || item?.logo || '',
    amount: item?.amount ?? 0,
    chainNum: Number(item?.chainNum ?? 0)
  };
}

function normalizeAddress(value = '') {
  return String(value || '').trim().toLowerCase();
}

function mergeRemoteAndLocal(remoteList = [], localList = []) {
  const merged = Array.isArray(remoteList) ? remoteList.map(normalizeNft) : [];
  const contractSet = new Set(
    merged
      .map((item) => normalizeAddress(item?.contractAddress))
      .filter(Boolean)
  );

  (Array.isArray(localList) ? localList : []).forEach((item) => {
    const normalized = normalizeNft(item);
    const key = normalizeAddress(normalized?.contractAddress);
    if (key && !contractSet.has(key)) {
      merged.push(normalized);
      contractSet.add(key);
    }
  });

  return merged;
}

export const useWalletNftStore = defineStore('wallet-nft', {
  state: () => defaultState(),
  getters: {
    hasNftByContract: (state) => (contractAddress) => {
      const target = normalizeAddress(contractAddress);
      if (!target) {
        return false;
      }
      return state.nftList.some((item) => normalizeAddress(item?.contractAddress) === target);
    },
    findNftByContract: (state) => (contractAddress) => {
      const target = normalizeAddress(contractAddress);
      if (!target) {
        return null;
      }
      return state.nftList.find((item) => normalizeAddress(item?.contractAddress) === target) || null;
    }
  },
  actions: {
    reset() {
      Object.assign(this, defaultState());
    },
    setCurrentNft(nft) {
      this.currentNft = nft ? normalizeNft(nft) : null;
    },
    setCurrentNftAsset(asset) {
      this.currentNftAsset = asset && typeof asset === 'object' ? { ...asset } : null;
    },
    setNftList(list, chainNum = null) {
      const profileStore = useProfileStore();
      const resolvedChainNum = chainNum ?? profileStore.currentNetwork?.chainNum;
      this.remoteList = Array.isArray(list) ? list.map(normalizeNft) : [];
      this.nftList = mergeRemoteAndLocal(this.remoteList, profileStore.getChainNfts(resolvedChainNum));
      if (this.currentNft?.contractAddress) {
        this.currentNft = this.findNftByContract(this.currentNft.contractAddress) || this.currentNft;
      }
    },
    rebuild(chainNum = null) {
      const profileStore = useProfileStore();
      const resolvedChainNum = chainNum ?? profileStore.currentNetwork?.chainNum;
      this.nftList = mergeRemoteAndLocal(this.remoteList, profileStore.getChainNfts(resolvedChainNum));
    },
    async fetchNftList(address, config = {}) {
      const profileStore = useProfileStore();
      const resolvedChainNum = profileStore.currentNetwork?.chainNum;
      if (!address) {
        this.setNftList([], resolvedChainNum);
        return [];
      }
      const result = await nftBalanceList({ address, chainNum: resolvedChainNum }, config);
      const list = Array.isArray(result?.list) ? result.list : Array.isArray(result) ? result : [];
      this.setNftList(list, resolvedChainNum);
      return this.nftList;
    },
    upsertLocalNft(nft, chainNum = null) {
      const profileStore = useProfileStore();
      const resolvedChainNum = chainNum ?? profileStore.currentNetwork?.chainNum;
      profileStore.addChainNft(normalizeNft(nft), resolvedChainNum);
      this.rebuild(resolvedChainNum);
      return this.nftList;
    },
    removeLocalNft(contractAddress, chainNum = null) {
      const profileStore = useProfileStore();
      const resolvedChainNum = chainNum ?? profileStore.currentNetwork?.chainNum;
      profileStore.removeChainNft(contractAddress, resolvedChainNum);
      this.rebuild(resolvedChainNum);
      return this.nftList;
    },
    dismissNft(contractAddress, chainNum = null) {
      const profileStore = useProfileStore();
      const resolvedChainNum = chainNum ?? profileStore.currentNetwork?.chainNum;
      const target = normalizeAddress(contractAddress);

      this.remoteList = this.remoteList.filter((item) => normalizeAddress(item?.contractAddress) !== target);
      this.nftList = this.nftList.filter((item) => normalizeAddress(item?.contractAddress) !== target);
      profileStore.removeChainNft(contractAddress, resolvedChainNum);

      if (normalizeAddress(this.currentNft?.contractAddress) === target) {
        this.currentNft = null;
      }
      if (normalizeAddress(this.currentNftAsset?.contractAddress) === target) {
        this.currentNftAsset = null;
      }

      return this.nftList;
    }
  }
});
